/**
 * Account: the user's profile settings page, rendered at /account/profile
 * (nested under UserHomepage; also the default redirect target of /account).
 * Shows readonly phone/email plus editable bio and instagram fields, and lets
 * the user upload a profile picture. Saves via api/user (UserAPI.editUser,
 * UserAPI.uploadProfilePicture) and syncs the result into contexts/UserContext.
 * Composes components/SubheadedPage and components/InputField; when the user
 * has no email, the email field shows an alert accessory linking to /verifyEmail.
 */
import React, { useState } from "react";
import { Pencil, Save } from "lucide-react";
import { setUserContext, useUserDispatch, useUserState } from "@/contexts/UserContext";
import { UserAPI } from "@/api/user";
import { SubheadedPage } from "@/components/SubheadedPage";
import InputField, { FieldConfig, FieldAccessoryConfig } from "@/components/InputField";
import { useNavigate } from "react-router-dom";
import { useError } from "@/contexts/ErrorContext";

const Account = () => {
    const user = useUserState();
    const userDispatch = useUserDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const { showError } = useError();
    const [instagram, setInstagram] = useState<string>(user!.instagram || "");
    const [bio, setBio] = useState<string>(user!.bio || "");
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const navigate = useNavigate();

    const FIELD_CONFIG: FieldConfig[] = [
        {
            key: "phone",
            label: "phone",
            type: "tel",
            readonly: true,
            readonlyValue: user!.phone,
            fieldState: null,
            setFieldState: null,
        },
        {
            key: "email",
            label: "email",
            type: "email",
            readonly: true,
            readonlyValue: user!.email ?? "",
            fieldState: null,
            setFieldState: null,
        },
        {
            key: "bio",
            label: "bio",
            type: "textarea",
            readonly: false,
            readonlyValue: null,
            fieldState: bio,
            setFieldState: setBio,
        },
        {
            key: "instagram",
            label: "instagram",
            type: "text",
            readonly: false,
            readonlyValue: null,
            fieldState: instagram,
            setFieldState: setInstagram,
        },
    ];

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setPreviewUrl(URL.createObjectURL(file));
            uploadProfilePicture(file);
        }
    };

    const checkIfChangesMade = () => {
        if ((user!.instagram || "") !== instagram.trim() || (user!.bio || "") !== bio.trim()) {
            return true;
        }
        return false;
    };

    const handleSaveClick = async () => {
        setLoading(true);
        try {
            const { user } = await UserAPI.editUser(instagram, bio);
            setUserContext(userDispatch, user);
        } catch (error: any) {
            showError(error as string);
        } finally {
            setLoading(false);
        }
    };

    const uploadProfilePicture = async (file: File) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("profilePicture", file);
            const { user } = await UserAPI.uploadProfilePicture(formData);
            setUserContext(userDispatch, user);
        } catch (error: any) {
            showError(error as string);
        } finally {
            setLoading(false);
        }
    };

    return (
        <SubheadedPage
            heading={user!.username}
            headerAction={
                <div className="w-full relative">
                    <div className="absolute top-[-24px] right-0 flex flex-row space-x-4">
                        <button
                            className={`p-2 rounded-full hover:bg-slate-100 transition-colors ${
                                checkIfChangesMade()
                                    ? "text-black"
                                    : "text-slate-400 cursor-not-allowed"
                            }`}
                            onClick={handleSaveClick}
                            disabled={loading || !checkIfChangesMade()}
                        >
                            <Save size={25} />
                        </button>
                    </div>
                </div>
            }
        >
            <div className="w-full h-full bg-slate-50 flex flex-col items-center md:items-start py-4 md:flex-row overflow-auto">
                {/* Profile Picture Section */}
                <div className="relative w-48 h-48 md:mx-8 md:mb-0 mb-8">
                    <div className="w-48 h-48 rounded-full overflow-hidden bg-gray-100">
                        {previewUrl || user!.profilePictureUrl ? (
                            <img
                                src={previewUrl || user!.profilePictureUrl!}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        ) : (
                            <div className="w-full h-full bg-slate-950" />
                        )}
                    </div>
                    <label
                        htmlFor="photo-upload"
                        className="absolute bottom-0 right-2 p-2 rounded-full bg-white shadow-md cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                        <Pencil className="w-5 h-5 text-gray-600" />
                        <input
                            id="photo-upload"
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                    </label>
                </div>

                {/* Form Fields */}
                <div className="flex flex-col w-64">
                    <div className="space-y-6">
                        {FIELD_CONFIG.map((field) => {
                            if (field.type === "email" && !user!.email) {
                                const accessoryConfig: FieldAccessoryConfig = {
                                    type: "alert",
                                    forType: field.type,
                                    tooltipMessage:
                                        "email address not added, click to verify and add",
                                    onClick: () => {
                                        navigate("/verifyEmail");
                                    },
                                };
                                return (
                                    <InputField
                                        key={field.key}
                                        fieldConfig={field}
                                        accessoryConfig={accessoryConfig}
                                    />
                                );
                            }
                            return <InputField key={field.key} fieldConfig={field} />;
                        })}
                    </div>
                </div>
            </div>
        </SubheadedPage>
    );
};

export default Account;
